import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';

function PrayerRequest() {
  const { t } = useTranslation();
  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setSubmissionError(null);

    try {
      const response = await fetch('https://formspree.io/f/xvzvaonl', { // formspree(used org email)
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          ...data,
          _subject: 'New Prayer Request',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit prayer request');
      }

      reset();
      setIsSubmitted(true);
      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (error) {
      console.error('Error submitting prayer request:', error);
      setSubmissionError(t('prayerRequest.form.errorMessage'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const prayerRequestVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } },
  };

  return (
    <div>
      <Helmet>
        <title>{t('prayerRequest.seoTitle')}</title>
        <meta name="description" content={t('prayerRequest.seoDescription')} />
      </Helmet>

      {/* Hero Section */}
      <section
        className="relative bg-cover bg-center h-[400px] flex items-center justify-center text-white"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('/assets/images/prayer.jpeg')`,
        }}
      >
        <div className="text-center px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {t('prayerRequest.heroTitle')}
          </h1>
          <p className="text-lg md:text-xl">
            {t('prayerRequest.heroSubtitle')}
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="bg-secondary py-16">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={prayerRequestVariants}
          >
            <h2 className="text-3xl md:text-4xl font-semibold text-textPrimary text-center mb-6">
              {t('prayerRequest.sectionTitle')}
            </h2>
            <p className="text-lg text-textPrimary text-center mb-12">
              {t('prayerRequest.sectionSubtitle')}
            </p>

            {isSubmitted ? (
              <div className="max-w-lg mx-auto text-center bg-green-100 text-green-800 p-4 rounded-lg mb-8">
                {t('prayerRequest.successMessage')}
              </div>
            ) : (
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="max-w-lg mx-auto bg-white shadow-md rounded-lg p-6"
                noValidate
              >
                {/* Honeypot (anti-spam) */}
                <input type="text" name="_gotcha" style={{ display: 'none' }} />

                {/* Name */}
                <div className="mb-4">
                  <label className="block text-textPrimary mb-2">
                    {t('prayerRequest.form.nameLabel')}
                  </label>
                  <input
                    type="text"
                    className={`w-full p-2 border rounded ${
                      errors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    {...register('name', { required: t('prayerRequest.form.nameRequired') })}
                  />
                  {errors.name && (
                    <p className="text-red-500 text-sm">{errors.name.message}</p>
                  )}
                </div>

                {/* Email */}
                <div className="mb-4">
                  <label className="block text-textPrimary mb-2">
                    {t('prayerRequest.form.emailLabel')}
                  </label>
                  <input
                    type="email"
                    className={`w-full p-2 border rounded ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    {...register('email', {
                      required: t('prayerRequest.form.emailRequired'),
                      pattern: {
                        value: /^[^@ ]+@[^@ ]+\.[^@ .]{2,}$/,
                        message: t('prayerRequest.form.emailInvalid'),
                      },
                    })}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm">{errors.email.message}</p>
                  )}
                </div>

                {/* Message (IMPORTANT FIX) */}
                <div className="mb-4">
                  <label className="block text-textPrimary mb-2">
                    {t('prayerRequest.form.requestLabel')}
                  </label>
                  <textarea
                    rows={5}
                    className={`w-full p-2 border rounded ${
                      errors.message ? 'border-red-500' : 'border-gray-300'
                    }`}
                    {...register('message', {
                      required: t('prayerRequest.form.requestRequired'),
                    })}
                  />
                  {errors.message && (
                    <p className="text-red-500 text-sm">{errors.message.message}</p>
                  )}
                </div>

                {/* Anonymous */}
                <div className="mb-4">
                  <label className="flex items-center">
                    <input type="checkbox" className="mr-2" {...register('anonymous')} />
                    {t('prayerRequest.form.anonymousLabel')}
                  </label>
                </div>

                {/* Error */}
                {submissionError && (
                  <div className="text-red-500 text-center mb-4">
                    {submissionError}
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary text-white px-4 py-2 rounded disabled:opacity-50"
                >
                  {isSubmitting
                    ? t('prayerRequest.form.submitting')
                    : t('prayerRequest.form.submitButton')}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default PrayerRequest;